<script setup lang="ts">
import type { Component } from 'vue';
import { ChevronRight } from 'lucide-vue-next';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';

withDefaults(defineProps<{
  icon?: Component;
  label?: string;
  trail?: ReadonlyArray<string>;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}>(), {
  icon: undefined,
  label: '',
  trail: () => [],
  loading: false,
  emptyTitle: 'Guide visual unavailable',
  emptyDescription: 'The written guide remains available alongside this step.',
});
</script>

<template>
  <div v-if="loading" class="w-full max-w-sm space-y-3 px-4 sm:space-y-5 sm:px-6">
    <Skeleton class="mx-auto size-12 rounded-xl sm:size-16 sm:rounded-2xl" />
    <div class="space-y-3">
      <Skeleton class="mx-auto h-4 w-48" />
      <Skeleton class="mx-auto h-7 w-64 rounded-full" />
    </div>
  </div>

  <EmptyState
    v-else-if="!icon && !label"
    embedded
    :title="emptyTitle"
    :description="emptyDescription"
  />

  <div v-else class="w-full max-w-sm space-y-3 px-4 text-center sm:space-y-5 sm:px-6">
    <div v-if="icon" class="mx-auto flex size-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary sm:size-16 sm:rounded-2xl">
      <component :is="icon" class="size-6 sm:size-8" />
    </div>
    <div class="space-y-1.5 sm:space-y-2">
      <p v-if="label" class="text-sm font-semibold tracking-tight text-foreground sm:text-base">{{ label }}</p>
      <div v-if="trail.length" class="mx-auto flex max-w-xs flex-wrap items-center justify-center gap-1.5 text-xs text-muted-foreground sm:gap-2">
        <template v-for="(item, index) in trail" :key="`${item}-${index}`">
          <ChevronRight v-if="index > 0" class="size-3.5 shrink-0" />
          <span class="rounded-full border border-border/70 bg-background px-2 py-0.5 sm:px-2.5 sm:py-1">{{ item }}</span>
        </template>
      </div>
    </div>
    <slot />
  </div>
</template>
